import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass } from "../components/FormField";
import { createAdminCompany, fetchAdminCompany, updateAdminCompany, type CompanyPayload } from "../api/insurerApi";

type Form = Record<
  "name" | "sector" | "rccm" | "city" | "address" | "phone" | "email" | "contact_name" | "contact_phone" | "contact_email",
  string
>;

const EMPTY: Form = {
  name: "", sector: "", rccm: "", city: "", address: "", phone: "", email: "", contact_name: "", contact_phone: "", contact_email: "",
};

const GROUPS: Array<{ title: string; fields: Array<{ key: keyof Form; label: string; placeholder?: string; type?: string; required?: boolean }> }> = [
  {
    title: "Identité",
    fields: [
      { key: "name", label: "Nom de l'entreprise", required: true },
      { key: "sector", label: "Secteur d'activité", placeholder: "Administration, ONG, mines…" },
      { key: "rccm", label: "RCCM" },
    ],
  },
  {
    title: "Coordonnées",
    fields: [
      { key: "city", label: "Ville", placeholder: "Kinshasa" },
      { key: "address", label: "Adresse", placeholder: "Avenue, numéro, commune" },
      { key: "phone", label: "Téléphone", placeholder: "+243 …" },
      { key: "email", label: "Email", type: "email" },
    ],
  },
  {
    title: "Interlocuteur (RH)",
    fields: [
      { key: "contact_name", label: "Nom" },
      { key: "contact_phone", label: "Téléphone" },
      { key: "contact_email", label: "Email", type: "email" },
    ],
  },
];

/**
 * Admin : création et modification d'une entreprise, comme un prestataire. L'entreprise est créée en amont,
 * dans le catalogue ; son rattachement à un assureur (avec ses packages autorisés) se fait depuis Assureurs.
 */
export function CompanyFormPage() {
  const { id } = useParams();
  const companyId = id ? Number(id) : null;
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [form, setForm] = useState<Form>(EMPTY);
  const [insurerName, setInsurerName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    fetchAdminCompany(companyId)
      .then((company) => {
        setForm({
          name: company.name,
          sector: company.sector ?? "",
          rccm: company.rccm ?? "",
          city: company.city ?? "",
          address: company.address ?? "",
          phone: company.phone ?? "",
          email: company.email ?? "",
          contact_name: company.contact_name ?? "",
          contact_phone: company.contact_phone ?? "",
          contact_email: company.contact_email ?? "",
        });
        setInsurerName(company.insurer_name);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Entreprise introuvable."));
  }, [companyId]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: CompanyPayload = { ...form, name: form.name.trim() };
      let savedId = companyId;
      if (companyId) {
        await updateAdminCompany(companyId, payload);
      } else {
        savedId = (await createAdminCompany(null, payload)).id;
      }
      showToast(companyId ? "Entreprise mise à jour." : "Entreprise créée : importez maintenant ses agents.");
      navigate(`/dashboard/companies/${savedId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const title = companyId ? `Modifier ${form.name || "l'entreprise"}` : "Ajouter une entreprise";

  return (
    <>
      <Breadcrumbs items={[{ label: "GESTION" }, { label: "Entreprises", href: "/dashboard/companies" }, { label: companyId ? "Modifier" : "Ajouter" }]} />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold text-foreground">{title}</h1>
        <Link
          to={companyId ? `/dashboard/companies/${companyId}` : "/dashboard/companies"}
          className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent/20 hover:border-accent"
        >
          <ArrowLeft size={14} />
          Retour
        </Link>
      </div>

      <div className="theme-card flex flex-col gap-6 p-5">
        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        {GROUPS.map((group) => (
          <div key={group.title}>
            <h2 className="mb-3 text-sm font-semibold text-foreground">{group.title}</h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
              {group.fields.map((field) => (
                <FormField key={field.key} label={field.label} required={field.required}>
                  <input
                    type={field.type ?? "text"}
                    value={form[field.key]}
                    placeholder={field.placeholder}
                    onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                    className={inputClass}
                  />
                </FormField>
              ))}
            </div>
          </div>
        ))}

        <div className="rounded-lg border border-border bg-card-muted/40 p-3 text-sm">
          <span className="font-semibold text-foreground">Assureur : </span>
          <span className="text-foreground">{insurerName ?? "aucun, l'entreprise reste dans le catalogue"}</span>
          <p className="mt-1 text-xs text-muted">
            Le rattachement (ou le transfert) se fait depuis Assureurs → Entreprises, en choisissant les packages autorisés
            pour l'entreprise. Ses agents la suivent ; ses factures passées restent chez l'ancien assureur.
          </p>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            disabled={saving || !form.name.trim()}
            onClick={() => void handleSave()}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
          >
            <Save size={15} />
            {companyId ? "Enregistrer" : "Créer l'entreprise"}
          </button>
        </div>
      </div>
    </>
  );
}
